import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Truck, Plus, Edit, Trash2, Search, AlertCircle, X } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { leafletDeliveryAPI, customersAPI, magazinesAPI } from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import { isDatePast } from '../utils/dateUtils';
import {
  filterLeafletDeliveries,
  getScheduleIssueOptions,
  getDeliveryIssueValues,
  buildIssueCloseMap,
  getCurrentIssueName,
} from '../utils/issueUtils';

// Simple error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('LeafletDelivery Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Layout>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Something went wrong</h3>
              <p className="mt-1 text-sm text-gray-500">
                There was an error loading the Leaflet Delivery page.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </Layout>
      );
    }

    return this.props.children;
  }
}

const LeafletDelivery = () => {
  const { canDelete, canCreateBookings } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leafletDeliveries, setLeafletDeliveries] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    issue: searchParams.get('issue') || '',
    publication: searchParams.get('magazine') || '',
    startIssue: searchParams.get('startIssue') || '',
    finishIssue: searchParams.get('finishIssue') || '',
  });
  const [availableIssues, setAvailableIssues] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);

  const [formData, setFormData] = useState({
    customer: '',
    magazine: '',
    startIssue: '',
    finishIssue: '',
    quantity: '',
    charge: '',
    note: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Debounced customer search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCustomers(customerSearch);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [customerSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.customer-search-container')) {
        setCustomerSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAvailableIssues = magazineId => {
    if (!magazineId || !Array.isArray(magazines)) {
      setAvailableIssues([]);
      return;
    }

    const magazine = magazines.find(m => m._id === magazineId);
    if (magazine && magazine.schedule && Array.isArray(magazine.schedule.issues)) {
      const availableIssues = magazine.schedule.issues.filter(
        issue => !isDatePast(issue.closeDate)
      );
      setAvailableIssues(Array.isArray(availableIssues) ? availableIssues : []);
    } else {
      setAvailableIssues([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching leaflet delivery data...');

      const [deliveriesResponse, magazinesResponse] = await Promise.all([
        leafletDeliveryAPI.getAll(),
        magazinesAPI.getAll(),
      ]);

      // Ensure we always get arrays
      const deliveries = Array.isArray(deliveriesResponse.data) ? deliveriesResponse.data : [];
      const magazines = Array.isArray(magazinesResponse.data) ? magazinesResponse.data : [];

      console.log('Data fetched successfully:', {
        deliveries: deliveries.length,
        magazines: magazines.length,
      });

      setLeafletDeliveries(deliveries);
      setMagazines(magazines);
    } catch (error) {
      console.error('Error fetching data:', error);
      showErrorToast(`Failed to load data: ${error.message}`);

      // Set empty arrays to prevent undefined errors
      setLeafletDeliveries([]);
      setMagazines([]);
    } finally {
      setLoading(false);
    }
  };

  const searchCustomers = async (searchTerm = '') => {
    if (formData.customer) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const response = await customersAPI.getAll({ search: searchTerm, limit: 100 });
      const customers = Array.isArray(response.data.customers) ? response.data.customers : [];
      setCustomerSearchResults(customers);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate that a customer is selected
    if (!formData.customer) {
      showErrorToast('Please select a customer');
      return;
    }

    try {
      if (editingItem) {
        await leafletDeliveryAPI.update(editingItem._id, formData);
        showSuccessToast('Leaflet delivery updated successfully');
      } else {
        await leafletDeliveryAPI.create(formData);
        showSuccessToast('Leaflet delivery created successfully');
      }

      setIsModalOpen(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving leaflet delivery:', error);
      showErrorToast(error.response?.data?.message || 'Failed to save leaflet delivery');
    }
  };

  const handleEdit = item => {
    setEditingItem(item);
    setFormData({
      customer: item.customer?._id,
      magazine: item.magazine?._id,
      startIssue: item.startIssue,
      finishIssue: item.finishIssue,
      quantity: item.quantity.toString(),
      charge: item.charge.toString(),
      note: item.note || '',
    });
    // Set customer search field with the current customer name
    if (item.customer?.name) {
      setCustomerSearch(item.customer.name);
    }
    // Load available issues for the selected magazine
    if (item.magazine?._id) {
      loadAvailableIssues(item.magazine._id);
    }
    setIsModalOpen(true);
  };

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this leaflet delivery?')) {
      return;
    }

    try {
      await leafletDeliveryAPI.delete(id);
      showSuccessToast('Leaflet delivery deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting leaflet delivery:', error);
      showErrorToast('Failed to delete leaflet delivery');
    }
  };

  const resetForm = () => {
    setFormData({
      customer: '',
      magazine: '',
      startIssue: '',
      finishIssue: '',
      quantity: '',
      charge: '',
      note: '',
    });
    setAvailableIssues([]);
    setCustomerSearch('');
    setCustomerSearchResults([]);
  };

  const openModal = () => {
    resetForm();
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    const paramMap = {
      search: 'search',
      issue: 'issue',
      publication: 'magazine',
      startIssue: 'startIssue',
      finishIssue: 'finishIssue',
    };
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, val]) => {
      if (val) newSearchParams.set(paramMap[key], val);
    });
    setSearchParams(newSearchParams);
  };

  const clearFilters = () => {
    setFilters({ search: '', issue: '', publication: '', startIssue: '', finishIssue: '' });
    setSearchParams(new URLSearchParams());
  };

  const issueOptions = useMemo(() => getScheduleIssueOptions(magazines), [magazines]);
  const startIssueOptions = useMemo(
    () => getDeliveryIssueValues(leafletDeliveries, 'startIssue'),
    [leafletDeliveries]
  );
  const finishIssueOptions = useMemo(
    () => getDeliveryIssueValues(leafletDeliveries, 'finishIssue'),
    [leafletDeliveries]
  );

  // Close-date lookup (from magazine schedules) used to label issue options as
  // Current / Closed, with closed issues greyed out — same as the Artwork page.
  const issueCloseMap = useMemo(() => buildIssueCloseMap(magazines), [magazines]);
  const currentIssueName = useMemo(() => getCurrentIssueName(issueCloseMap), [issueCloseMap]);

  const renderIssueOption = issue => {
    const closeDate = issueCloseMap.get(issue);
    const isClosed = closeDate ? isDatePast(closeDate) : false;
    const isCurrent = currentIssueName && issue === currentIssueName;
    const labels = [];
    if (isCurrent) labels.push('Current');
    if (isClosed) labels.push('Closed');

    return (
      <option key={issue} value={issue} style={{ color: isClosed ? '#9ca3af' : 'inherit' }}>
        {labels.length > 0 ? `${issue} (${labels.join(', ')})` : issue}
      </option>
    );
  };

  const filteredDeliveries = useMemo(
    () => filterLeafletDeliveries(leafletDeliveries, filters),
    [leafletDeliveries, filters]
  );

  const hasActiveFilters = Object.values(filters).some(value => value !== '');
  const totalEntries = filteredDeliveries.length;
  const totalValue = filteredDeliveries.reduce((sum, d) => sum + (d.charge || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="ml-3 text-gray-600">Loading leaflet deliveries...</p>
          </div>
        </div>
      </Layout>
    );
  }

  console.log('Rendering LeafletDelivery component:', {
    deliveries: leafletDeliveries.length,
    magazines: magazines.length,
    loading,
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Leaflet Delivery</h1>
            <p className="mt-2 text-gray-600">Manage leaflet insertion bookings</p>
          </div>
          {canCreateBookings() && (
            <button
              onClick={openModal}
              className="inline-flex items-center px-4 py-2 mt-4 sm:mt-0 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Leaflet Delivery
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>

            <div className="mb-4 w-full">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search Customers"
                  value={filters.search}
                  onChange={e => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <select
              value={filters.issue}
              onChange={e => handleFilterChange('issue', e.target.value)}
              className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Issues</option>
              {issueOptions.map(renderIssueOption)}
            </select>

            <select
              value={filters.publication}
              onChange={e => handleFilterChange('publication', e.target.value)}
              className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Publications</option>
              {Array.isArray(magazines)
                ? magazines.map(magazine => (
                    <option key={magazine._id} value={magazine._id}>
                      {magazine.name}
                    </option>
                  ))
                : null}
            </select>

            <select
              value={filters.startIssue}
              onChange={e => handleFilterChange('startIssue', e.target.value)}
              className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Start Issues</option>
              {startIssueOptions.map(renderIssueOption)}
            </select>

            <select
              value={filters.finishIssue}
              onChange={e => handleFilterChange('finishIssue', e.target.value)}
              className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Finish Issues</option>
              {finishIssueOptions.map(renderIssueOption)}
            </select>
          </div>

            {hasActiveFilters && (
              <div className="mt-4">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <X className="mr-1 w-4 h-4" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Totals */}
        {totalEntries > 0 && (
          <div className="mb-6 grid grid-cols-1 gap-4 p-4 bg-gray-50 rounded-lg sm:grid-cols-2">
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">{totalEntries}</div>
              <div className="text-sm text-gray-500">Total Entries</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-green-600">
                £{totalValue.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Total Value</div>
            </div>
          </div>
        )}

        {/* Leaflet Deliveries Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {filteredDeliveries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Publication
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issues
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Note
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Charge
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeliveries.map(delivery => (
                    <tr key={delivery._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">
                            {delivery.customer?.name}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500">
                          {delivery.customer?.businessCategory}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {delivery.magazine?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {delivery.startIssue} - {delivery.finishIssue}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="relative group">
                          {delivery.note ? (
                            <>
                              <span className="cursor-help">
                                {delivery.note.length > 15
                                  ? `${delivery.note.substring(0, 15)}...`
                                  : delivery.note}
                              </span>
                              {delivery.note.length > 15 && (
                                <div className="absolute z-50 invisible group-hover:visible bg-gray-900 text-white text-xs rounded-lg py-2 px-3 bottom-full left-0 mb-2 whitespace-normal w-64 shadow-lg">
                                  {delivery.note}
                                  <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {delivery.quantity.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        £{delivery.charge.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap  text-sm font-medium">
                        {canCreateBookings() && (
                          <button
                            onClick={() => handleEdit(delivery)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        {canDelete() && (
                          <button
                            onClick={() => handleDelete(delivery._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Truck className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No leaflet deliveries</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new leaflet delivery.
              </p>
              {canCreateBookings() && (
                <div className="mt-6">
                  <button
                    onClick={openModal}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Leaflet Delivery
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingItem ? 'Edit Leaflet Delivery' : 'Add New Leaflet Delivery'}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                    <div className="relative customer-search-container">
                      <input
                        type="text"
                        value={customerSearch}
                        onChange={e => {
                          const value = e.target.value;
                          setCustomerSearch(value);

                          // If user clears the search or changes it, clear the selected customer
                          if (!value || (formData.customer && value !== customerSearch)) {
                            setFormData({ ...formData, customer: '' });
                          }
                        }}
                        onFocus={() => {
                          // Load all customers when field is clicked
                          if (!formData.customer) {
                            searchCustomers(customerSearch);
                          }
                        }}
                        placeholder="Search customers..."
                        required={!formData.customer}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          formData.customer ? 'bg-green-50 border-green-300 pr-10' : ''
                        }`}
                      />

                      {/* Clear button when customer is selected */}
                      {formData.customer && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, customer: '' });
                            setCustomerSearch('');
                            setCustomerSearchResults([]);
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}

                      {/* Loading spinner */}
                      {customerSearchLoading && !formData.customer && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                      {customerSearchResults.length > 0 && !formData.customer && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {customerSearchResults.map(customer => (
                            <div
                              key={customer._id}
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  customer: customer._id,
                                });
                                setCustomerSearch(customer.name);
                                setCustomerSearchResults([]);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{customer.name}</div>
                              {customer.businessCategory && (
                                <div className="text-sm text-gray-500">
                                  {customer.businessCategory}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {customerSearch &&
                        customerSearchResults.length === 0 &&
                        !customerSearchLoading &&
                        !formData.customer && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-gray-500 text-sm">
                            No customers found. Try a different search term.
                          </div>
                        )}
                    </div>
                    {formData.customer && (
                      <div className="mt-1 text-sm text-green-600">✓ Customer selected</div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Publication
                    </label>
                    <select
                      value={formData.magazine}
                      onChange={e => {
                        const magazineId = e.target.value;
                        setFormData({
                          ...formData,
                          magazine: magazineId,
                          startIssue: '', // Reset issue selections when magazine changes
                          finishIssue: '',
                        });
                        loadAvailableIssues(magazineId);
                      }}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Publication</option>
                      {Array.isArray(magazines)
                        ? magazines.map(magazine => (
                            <option key={magazine._id} value={magazine._id}>
                              {magazine.name}
                            </option>
                          ))
                        : null}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Issue
                      </label>
                      <select
                        value={formData.startIssue}
                        onChange={e =>
                          setFormData({
                            ...formData,
                            startIssue: e.target.value,
                          })
                        }
                        required
                        disabled={
                          !formData.magazine ||
                          !Array.isArray(availableIssues) ||
                          availableIssues.length === 0
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="">-</option>
                        {Array.isArray(availableIssues)
                          ? availableIssues.map(issue => (
                              <option key={issue._id || issue.name} value={issue.name}>
                                {issue.name}
                              </option>
                            ))
                          : null}
                      </select>
                      {!formData.magazine && (
                        <p className="text-xs text-gray-500 mt-1">Select a publication first</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Finish Issue
                      </label>
                      <select
                        value={formData.finishIssue}
                        onChange={e =>
                          setFormData({
                            ...formData,
                            finishIssue: e.target.value,
                          })
                        }
                        required
                        disabled={
                          !formData.magazine ||
                          !Array.isArray(availableIssues) ||
                          availableIssues.length === 0
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="">-</option>
                        {Array.isArray(availableIssues)
                          ? availableIssues.map(issue => (
                              <option key={issue._id || issue.name} value={issue.name}>
                                {issue.name}
                              </option>
                            ))
                          : null}
                      </select>
                      {!formData.magazine && (
                        <p className="text-xs text-gray-500 mt-1">Select a publication first</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        value={formData.quantity}
                        onChange={e =>
                          setFormData({
                            ...formData,
                            quantity: e.target.value,
                          })
                        }
                        required
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Charge (£)
                      </label>
                      <input
                        type="number"
                        value={formData.charge}
                        onChange={e =>
                          setFormData({
                            ...formData,
                            charge: e.target.value,
                          })
                        }
                        required
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Note (Optional)
                    </label>
                    <textarea
                      value={formData.note}
                      onChange={e => setFormData({ ...formData, note: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional notes..."
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                    >
                      {editingItem ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Wrap the component with error boundary
const LeafletDeliveryWithErrorBoundary = () => (
  <ErrorBoundary>
    <LeafletDelivery />
  </ErrorBoundary>
);

export default LeafletDeliveryWithErrorBoundary;
